import {FC, useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import moment from 'moment'
import {useIntl} from 'react-intl'
import {updateQuery} from '../slices/hrFilterSlice'
import {DateRangePicker} from 'react-bootstrap-daterangepicker'
import {useAppDispatch, useAppSelector} from '../../../hooks/redux'

type Props = {
  language?: 'en' | 'ru'
  timeZone?: string
  rangeId: string
  onlyIcon: boolean
}

export const DateRangePickerHr: FC<Props> = ({language, timeZone, rangeId, onlyIcon}) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const pickerRef = useRef<any>(null)
  const query = useAppSelector((state) => state.hrFilter.query)

  useEffect(() => {
    if (!query) return

    if (query.period) {
      const [start, end] = query.period.split(',')
      handleCallback(moment.utc(start), moment.utc(end))
    } else {
      handleCallback(moment.utc(), moment.utc())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [state, setState] = useState<{
    start: moment.Moment
    end: moment.Moment
  }>({
    start: moment().startOf('day'),
    end: moment().endOf('day'),
  })

  const localeSettings = {
    format: 'DD.MM.YYYY',
    separator: ' - ',
    applyLabel: intl.formatMessage({id: 'FILTERS.BUTTON.APPLY'}),
    cancelLabel: intl.formatMessage({id: 'COMMON.BUTTON.CLOSE'}),
    timeZone: timeZone,
    customRangeLabel: intl.formatMessage({id: 'DATERANGEPICKER.CUSTOM'}),
    daysOfWeek: [
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.SU'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.MO'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.TU'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.WE'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.TH'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.FR'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.SA'}),
    ],
    monthNames: [
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.JENUARY'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.FEBRUARY'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.MARCH'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.APRIL'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.MAY'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.JUNE'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.JULY'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.AUGUST'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.SEPTEMBER'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.OCTOBER'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.NOVEMBER'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.DECEMBER'}),
    ],
    firstDay: 1,
  }

  const rangeEngSettings = {
    Today: [moment().toDate(), moment().toDate()],
    Yesterday: [moment().subtract(1, 'days').toDate(), moment().subtract(1, 'days').toDate()],
    'Last 7 Days': [moment().subtract(6, 'days').toDate(), moment().toDate()],
    'Last 30 Days': [moment().subtract(29, 'days').toDate(), moment().toDate()],
    'This Month': [moment().startOf('month').toDate(), moment().endOf('month').toDate()],
    'Last Month': [
      moment().subtract(1, 'month').startOf('month').toDate(),
      moment().subtract(1, 'month').endOf('month').toDate(),
    ],
  }

  const rangeRusSettings = {
    Сегодня: [moment().toDate(), moment().toDate()],
    Вчера: [moment().subtract(1, 'days').toDate(), moment().subtract(1, 'days').toDate()],
    'Последние 7 дней': [moment().subtract(6, 'days').toDate(), moment().toDate()],
    'Поcледние 30 дней': [moment().subtract(29, 'days').toDate(), moment().toDate()],
    'Этот месяц': [moment().startOf('month').toDate(), moment().endOf('month').toDate()],
    'Прошлый месяц': [
      moment().subtract(1, 'month').startOf('month').toDate(),
      moment().subtract(1, 'month').endOf('month').toDate(),
    ],
  }

  const handleCallback = (startUtc: moment.Moment, endUtc: moment.Moment) => {
    const tz = timeZone || moment.tz.guess()

    const startLocal = startUtc.clone().tz(tz).startOf('day')
    const endLocal = endUtc.clone().tz(tz).endOf('day')

    const startForApi = startLocal.clone().utc().format('YYYY-MM-DDTHH:mm:ss')
    const endForApi = endLocal.clone().utc().format('YYYY-MM-DDTHH:mm:ss')

    setState({start: startUtc, end: endUtc})
    dispatch(updateQuery({period: `${startForApi},${endForApi}`}))
  }

  useEffect(() => {
    if (pickerRef.current) {
      pickerRef.current.setStartDate(state.start.toDate())
      pickerRef.current.setEndDate(state.end.toDate())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  return (
    <DateRangePicker
      ref={pickerRef}
      key={language}
      initialSettings={{
        startDate: state.start.toDate(),
        endDate: state.end.toDate(),
        showDropdowns: true,
        timeZone: timeZone,
        maxDate: moment().toDate(),
        ranges: language === 'ru' ? rangeRusSettings : rangeEngSettings,
        locale: localeSettings,
        alwaysShowCalendars: true,
        minYear: 2024,
      }}
      onCallback={handleCallback}
    >
      <div className='input-group input-group-solid'>
        <span
          className={clsx('input-group-text', {
            'btn btn-small btn-light': onlyIcon === true,
          })}
        >
          <i className='fa fa-calendar p-0' />
        </span>
        {onlyIcon === false && (
          <input
            id={'reportRange' + rangeId}
            type='text'
            className='form-control form-control-lg form-control-solid text-gray-900 text-center test-sm cursor-pointer px-3'
            placeholder='Период'
            value={`${state.start
              .clone()
              .tz(timeZone || moment.tz.guess())
              .format('DD.MM.YYYY')} - ${state.end
              .clone()
              .tz(timeZone || moment.tz.guess())
              .format('DD.MM.YYYY')}`}
            readOnly
          />
        )}
      </div>
    </DateRangePicker>
  )
}
